// useAcceptPendingBaseInvite.js

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

import { createSetting, updateSetting } from 'services/settings/SettingWriteService';
import { acceptBaseInvite } from 'services/bases/BaseInviteService';
import { getBase } from 'services/bases/BaseReadService';
import {
  captureInviteTokenFromSearch,
  clearPendingBaseInviteToken,
  clearStoredPostAuthRedirect,
  readPendingBaseInviteToken,
} from 'services/bases/baseInviteRedirectStorage';
import { buildActiveBaseSettingPatch } from 'utilities/baseSettingsKeys';

let sharedAcceptPromise = null;

function mapAcceptInviteError(error) {
  const code = String(error?.code || '');
  const message = String(error?.message || '').trim();

  if (code.includes('permission-denied')) {
    if (message.includes('not addressed')) {
      return 'This invite is for a different Ai account. Sign out and sign in with the invited email.';
    }
    if (message.includes('does not match')) {
      return 'The email on your account does not match this invite. Sign in with the invited email address.';
    }
    return 'You are not allowed to accept this invite with the current account.';
  }

  if (code.includes('deadline-exceeded')) {
    return 'This invite has expired. Ask the base owner to send a new invite.';
  }

  if (code.includes('failed-precondition')) {
    return message || 'This invite is no longer valid.';
  }

  if (code.includes('not-found')) {
    return 'This invite link is invalid or has already been used.';
  }

  return message || 'Unable to accept this invite right now. Please try again.';
}

async function acceptPendingInviteOnce({
  token,
  user,
  userSettings,
  setUserSettings,
  setActiveBase,
}) {
  if (sharedAcceptPromise) {
    return sharedAcceptPromise;
  }

  sharedAcceptPromise = (async () => {
    const result = await acceptBaseInvite({ token });
    const baseKey = String(result?.baseKey || '').trim();

    if (!baseKey) {
      throw new Error('Invite accepted, but base key was missing.');
    }

    const base = await getBase(baseKey);
    const settingsPatch = {
      ...buildActiveBaseSettingPatch(baseKey),
    };

    try {
      await updateSetting(user.key, settingsPatch);
    } catch (error) {
      await createSetting(user.key, {
        ...(userSettings || {}),
        ...settingsPatch,
        userKey: user.key,
      });
    }

    setUserSettings?.({
      ...(userSettings || {}),
      ...settingsPatch,
      userKey: user.key,
    });

    if (base) {
      setActiveBase?.(base);
    }

    clearPendingBaseInviteToken();
    clearStoredPostAuthRedirect();

    return {
      baseKey,
      alreadyMember: Boolean(result?.alreadyMember),
    };
  })().finally(() => {
    sharedAcceptPromise = null;
  });

  return sharedAcceptPromise;
}

/**
 * Accepts a pending base invite after auth, using a persisted token
 * so the flow survives login, email verification, and page reloads.
 */
export default function useAcceptPendingBaseInvite() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isEmailVerified, userSettings, setUserSettings } = useUser();
  const { setActiveBase } = useBase();

  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [acceptedBaseKey, setAcceptedBaseKey] = useState('');
  const [hasPendingToken, setHasPendingToken] = useState(
    () => Boolean(readPendingBaseInviteToken())
  );
  const autoAcceptAttemptedRef = useRef(false);

  useEffect(() => {
    const token = captureInviteTokenFromSearch(location.search);
    setHasPendingToken(Boolean(token || readPendingBaseInviteToken()));
  }, [location.search]);

  const runAccept = useCallback(async () => {
    const token = readPendingBaseInviteToken();
    if (!token || !user?.key || !isEmailVerified) {
      return null;
    }

    setStatus('accepting');
    setErrorMessage('');

    try {
      const result = await acceptPendingInviteOnce({
        token,
        user,
        userSettings,
        setUserSettings,
        setActiveBase,
      });

      setAcceptedBaseKey(result.baseKey);
      setHasPendingToken(false);
      setStatus('success');

      if (location.pathname.startsWith('/invite')) {
        window.setTimeout(() => {
          navigate('/', { replace: true });
        }, 700);
      }

      return result;
    } catch (error) {
      console.error('[useAcceptPendingBaseInvite] unable to accept invite:', error);
      setStatus('error');
      setErrorMessage(mapAcceptInviteError(error));
      return null;
    }
  }, [
    isEmailVerified,
    location.pathname,
    navigate,
    setActiveBase,
    setUserSettings,
    user,
    userSettings,
  ]);

  useEffect(() => {
    if (!user?.key) {
      autoAcceptAttemptedRef.current = false;
    }
  }, [user?.key]);

  useEffect(() => {
    const token = readPendingBaseInviteToken();
    if (!token || !user?.key || !isEmailVerified || autoAcceptAttemptedRef.current) {
      return undefined;
    }

    autoAcceptAttemptedRef.current = true;
    runAccept();

    return undefined;
  }, [isEmailVerified, runAccept, user?.key]);

  const retry = useCallback(() => {
    autoAcceptAttemptedRef.current = true;
    runAccept();
  }, [runAccept]);

  const dismiss = useCallback(() => {
    clearPendingBaseInviteToken();
    autoAcceptAttemptedRef.current = false;
    setHasPendingToken(false);
    setStatus('idle');
    setErrorMessage('');
    setAcceptedBaseKey('');
  }, []);

  return {
    status,
    errorMessage,
    acceptedBaseKey,
    hasPendingToken,
    retry,
    dismiss,
  };
}
